// TODO: server-side preview generation for office files is out of scope for this branch — see WORKSPACE_CONTRACT.md "Out of scope". When backend supplies preview/sheets/slides we render them; otherwise we show file chrome only.
/**
 * OfficeFileRenderer — renders `docx`, `xlsx`, `pptx`, and `pdf` artifacts.
 *
 * All four share `OfficeFilePayload`. Common header carries filename (from
 * `file_path` basename), human-readable file size, MIME type, and an
 * "Open in {tool}" affordance when the payload exposes an `external_url`
 * (M365 / SharePoint jump-out). The body is dispatched by `artifact.type`:
 *
 *   - docx  → big FileText icon + optional markdown text-excerpt preview
 *   - xlsx  → big Sheet icon + optional sheet list and first-N-rows preview
 *   - pptx  → big Presentation icon + slide thumbnail grid (first 8 + "+N more")
 *   - pdf   → big FileType icon + optional page-1 image preview, page count chip
 *
 * Default actions when `artifact.actions` is empty:
 *   - download (always)
 *   - open_external (only when external_url is set, primary)
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useMemo } from "react"

import type {
  ActionTargetSystem,
  Artifact,
  ArtifactAction,
  OfficeFilePayload,
  SheetMeta,
  SlideMeta,
} from "@/api/models"
import type { LucideIcon } from "@/components/icons"

import { ExternalLink, FileText, FileType, Presentation, Sheet } from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { ArtifactFrame } from "./ArtifactFrame"

type OfficeArtifactType = "docx" | "xlsx" | "pptx" | "pdf"

const TYPE_ICON: Record<OfficeArtifactType, LucideIcon> = {
  docx: FileText,
  xlsx: Sheet,
  pptx: Presentation,
  pdf: FileType,
}

/** External-system tools the M365 jump-out maps onto. We pick a sensible
 *  default for each office type when the payload doesn't explicitly tell us
 *  which app to open in (the contract's `ActionTarget.system` lives on
 *  `action_receipt`, not on office payloads). */
const TYPE_TOOL: Record<OfficeArtifactType, ActionTargetSystem> = {
  docx: "m365_word",
  xlsx: "m365_excel",
  pptx: "m365_powerpoint",
  pdf: "sharepoint",
}

const SYSTEM_LABEL: Record<ActionTargetSystem, string> = {
  m365_outlook: "Outlook",
  m365_word: "Word",
  m365_excel: "Excel",
  m365_powerpoint: "PowerPoint",
  servicedesk: "ServiceDesk",
  netbox: "NetBox",
  sharepoint: "SharePoint",
  other: "external",
}

const MAX_SLIDE_THUMBS = 8

/** SI-style human-readable byte size: 1500 → "1.5 KB", 2_500_000 → "2.5 MB". */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B"
  if (bytes < 1000) return `${bytes} B`
  const units = ["KB", "MB", "GB", "TB"]
  let value = bytes / 1000
  let unit = units[0]
  for (let i = 0; i < units.length; i += 1) {
    unit = units[i]
    if (value < 1000 || i === units.length - 1) break
    value /= 1000
  }
  // 1 decimal under 100, none beyond — keeps "1.5 MB" but "234 MB".
  const rounded = value < 100 ? Math.round(value * 10) / 10 : Math.round(value)
  return `${rounded} ${unit}`
}

function basename(path: string): string {
  if (!path) return ""
  // Strip query / hash; collapse trailing slashes; pick last segment.
  const cleaned = path.split(/[?#]/, 1)[0].replace(/\/+$/, "")
  const idx = cleaned.lastIndexOf("/")
  return idx >= 0 ? cleaned.slice(idx + 1) : cleaned
}

function buildDefaultActions(payload: OfficeFilePayload): ArtifactAction[] {
  const actions: ArtifactAction[] = [{ id: "download", label: "Download", icon: "download" }]
  if (payload.external_url) {
    const tool = TYPE_TOOL[payload.type as OfficeArtifactType]
    actions.push({
      id: "open_external",
      label: `Open in ${SYSTEM_LABEL[tool]}`,
      icon: "external-link",
      primary: true,
    })
  }
  return actions
}

export interface OfficeFileRendererProps {
  artifact: Artifact & { payload: OfficeFilePayload }
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
}

function FileChrome({ payload, type }: { payload: OfficeFilePayload; type: OfficeArtifactType }) {
  const filename = basename(payload.file_path) || "(unnamed file)"
  const size = formatBytes(payload.file_size_bytes)
  const tool = TYPE_TOOL[type]
  const toolLabel = SYSTEM_LABEL[tool]

  return (
    <div className="flex items-start gap-3" data-testid="office-file-chrome">
      <BigTypeIcon type={type} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span
            className="truncate text-sm font-medium"
            data-testid="office-file-name"
            title={filename}
          >
            {filename}
          </span>
          <span className="text-muted-foreground text-xs" data-testid="office-file-size">
            ({size})
          </span>
        </div>
        <p className="text-muted-foreground mt-0.5 text-xs" data-testid="office-file-mime">
          {payload.mime_type}
        </p>
        {payload.external_url ? (
          <Button
            asChild
            className="mt-2 h-7 gap-1 px-2 text-xs"
            data-testid="office-file-open-external"
            size="sm"
            variant="outline"
          >
            <a href={payload.external_url} rel="noreferrer noopener" target="_blank">
              <ExternalLink aria-hidden className="size-3" />
              <span>Open in {toolLabel}</span>
            </a>
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function BigTypeIcon({ type }: { type: OfficeArtifactType }) {
  const Icon = TYPE_ICON[type]
  return (
    <div
      aria-hidden
      data-icon-type={type}
      data-testid="office-file-big-icon"
      className={cn(
        "flex size-12 shrink-0 items-center justify-center rounded-md border",
        type === "docx" && CATEGORY_TONES[0],
        type === "xlsx" && CATEGORY_TONES[4],
        type === "pptx" && CATEGORY_TONES[1],
        type === "pdf" && CATEGORY_TONES[2]
      )}
    >
      <Icon className="size-7" />
    </div>
  )
}

function DocxBody({ payload }: { payload: OfficeFilePayload }) {
  const preview = payload.preview
  const hasExcerpt =
    preview?.kind === "text_excerpt" && typeof preview.data === "string" && preview.data.length > 0

  return (
    <div className="flex flex-col gap-3" data-testid="office-file-docx-body">
      <FileChrome payload={payload} type="docx" />
      {hasExcerpt ? (
        <div
          className="bg-muted/30 max-h-72 overflow-y-auto rounded-md border p-3"
          data-testid="office-file-text-excerpt"
        >
          <MarkdownRenderer content={preview!.data as string} />
        </div>
      ) : null}
    </div>
  )
}

function SheetsList({ sheets }: { sheets: SheetMeta[] }) {
  return (
    <ul className="flex flex-col gap-1" data-testid="office-file-sheets">
      {sheets.map((s) => (
        <li
          key={s.name}
          className="bg-muted/30 flex items-center justify-between rounded border px-2 py-1 text-xs"
          data-testid="office-file-sheet-row"
        >
          <span className="truncate font-medium">{s.name}</span>
          <span className="text-muted-foreground">
            · {s.row_count.toLocaleString("en-US")} row{s.row_count === 1 ? "" : "s"}
          </span>
        </li>
      ))}
    </ul>
  )
}

function FirstRowsTable({ rows }: { rows: string[] }) {
  // Per the contract schema, `OfficeFilePreview.data` for `first_n_rows` is
  // `string[]` — each entry is one pre-rendered row. We display as a single-
  // column table so users see an honest "first N lines of the sheet" peek.
  return (
    <div className="overflow-x-auto rounded-md border" data-testid="office-file-rows">
      <table className="w-full text-xs">
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b last:border-b-0" data-testid="office-file-row">
              <td className="px-2 py-1 align-top font-mono whitespace-pre-wrap">{row}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function XlsxBody({ payload }: { payload: OfficeFilePayload }) {
  const sheets = payload.sheets ?? []
  const preview = payload.preview
  const rows: string[] | null =
    preview?.kind === "first_n_rows" && Array.isArray(preview.data) ? preview.data : null

  return (
    <div className="flex flex-col gap-3" data-testid="office-file-xlsx-body">
      <FileChrome payload={payload} type="xlsx" />
      {sheets.length > 0 ? <SheetsList sheets={sheets} /> : null}
      {rows && rows.length > 0 ? <FirstRowsTable rows={rows} /> : null}
    </div>
  )
}

function SlideThumbnail({ slide }: { slide: SlideMeta }) {
  if (slide.thumbnail_url) {
    return (
      <div
        className="bg-muted/40 flex flex-col gap-1 rounded border p-1"
        data-testid="office-file-slide-thumb"
      >
        <img
          alt={slide.title || `Slide ${slide.index}`}
          className="aspect-video w-full rounded-sm object-cover"
          loading="lazy"
          src={slide.thumbnail_url}
        />
        <span className="text-muted-foreground truncate px-0.5 text-[11px]">
          {slide.index}. {slide.title}
        </span>
      </div>
    )
  }
  return (
    <div
      className="bg-muted/40 flex aspect-video flex-col justify-between rounded border p-2 text-xs"
      data-testid="office-file-slide-thumb"
    >
      <span className="text-muted-foreground text-[10px]">#{slide.index}</span>
      <span className="line-clamp-3 font-medium">{slide.title || "Untitled slide"}</span>
    </div>
  )
}

function PptxBody({ payload }: { payload: OfficeFilePayload }) {
  const slides = payload.slides ?? []
  const visible = slides.slice(0, MAX_SLIDE_THUMBS)
  const overflow = Math.max(0, slides.length - MAX_SLIDE_THUMBS)

  return (
    <div className="flex flex-col gap-3" data-testid="office-file-pptx-body">
      <FileChrome payload={payload} type="pptx" />
      {visible.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div
            className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4"
            data-testid="office-file-slides-grid"
          >
            {visible.map((slide) => (
              <SlideThumbnail key={slide.index} slide={slide} />
            ))}
          </div>
          {overflow > 0 ? (
            <Badge
              className="self-start"
              data-testid="office-file-slides-overflow"
              variant="secondary"
            >
              +{overflow} more
            </Badge>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function PdfBody({ payload }: { payload: OfficeFilePayload }) {
  const preview = payload.preview
  // Per the contract schema, `page_image` data is base64-encoded for a
  // page-1 thumbnail. We accept either a raw base64 string (assume PNG) or
  // a full data: URI.
  let imageSrc: string | null = null
  if (preview?.kind === "page_image" && typeof preview.data === "string" && preview.data) {
    imageSrc = preview.data.startsWith("data:")
      ? preview.data
      : `data:image/png;base64,${preview.data}`
  }
  const pageCount = payload.page_count ?? null

  return (
    <div className="flex flex-col gap-3" data-testid="office-file-pdf-body">
      <FileChrome payload={payload} type="pdf" />
      {pageCount != null ? (
        <Badge className="self-start" data-testid="office-file-page-count" variant="secondary">
          {pageCount.toLocaleString("en-US")} page{pageCount === 1 ? "" : "s"}
        </Badge>
      ) : null}
      {imageSrc ? (
        <div className="overflow-hidden rounded-md border">
          <img
            alt="PDF page 1 preview"
            className="block max-h-72 w-auto object-contain"
            data-testid="office-file-page-image"
            src={imageSrc}
          />
        </div>
      ) : null}
    </div>
  )
}

export function OfficeFileRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
}: OfficeFileRendererProps) {
  const framedArtifact = useMemo<Artifact>(() => {
    const hasActions = (artifact.actions ?? []).length > 0
    const actions = hasActions ? artifact.actions : buildDefaultActions(artifact.payload)
    return { ...artifact, actions }
  }, [artifact])

  const { payload } = artifact
  const type = payload.type as OfficeArtifactType

  let body
  if (type === "docx") {
    body = <DocxBody payload={payload} />
  } else if (type === "xlsx") {
    body = <XlsxBody payload={payload} />
  } else if (type === "pptx") {
    body = <PptxBody payload={payload} />
  } else {
    body = <PdfBody payload={payload} />
  }

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div data-office-type={type} data-testid="office-file-renderer-body">
        {body}
      </div>
    </ArtifactFrame>
  )
}
